import { motion, Transition, Variants } from "motion/react"

// top/bottom/left/right tick marks each span 19 units along their axis
// (e.g. "top" runs from y29.4058 to y48.4058) - on hover they slide inward by
// that same distance, overshooting by 4 before settling back, for a bounce
// that matches the shape of the other tool animations.
const TRAVEL = 19
const OVERSHOOT = TRAVEL + 4

// Their visual length (path length + the round line-cap on each end) shrinks
// from 22 to 15 by the settle point. Scaling the path would also thin the
// stroke - vector-effect="non-scaling-stroke" is meant to prevent that but
// doesn't reliably survive a Motion-driven transform across browsers - so
// instead each end is inset by half the difference directly in the path's
// own coordinates, leaving the 3px stroke untouched.
const INSET = (22 - 15) / 2

const eyeVariants: Variants = {
  rest: { scale: 1 },
  hover: { scale: [1, 1.21, 1.17], transition: { duration: 0.3, ease: "easeOut", times: [0, 0.3, 1] } },
}

const rotateVariants: Variants = {
  rest: { rotate: 0 },
  hover: { rotate: [0, -15, -13], transition: { duration: 0.3, ease: "easeOut", times: [0, 0.3, 1] } },
}

const topVariants: Variants = {
  rest: { y: 0, d: "M209 29.4058V48.4058" },
  hover: {
    y: [0, OVERSHOOT, TRAVEL],
    d: `M209 ${29.4058 + INSET}V${48.4058 - INSET}`,
    transition: {
      y: { duration: 0.3, ease: "easeOut", times: [0, 0.3, 1] },
      d: { duration: 0.3, ease: "easeOut" },
    },
  },
}

const bottomVariants: Variants = {
  rest: { y: 0, d: "M209 229.406V248.406" },
  hover: {
    y: [0, -OVERSHOOT, -TRAVEL],
    d: `M209 ${229.406 + INSET}V${248.406 - INSET}`,
    transition: {
      y: { duration: 0.3, ease: "easeOut", times: [0, 0.3, 1] },
      d: { duration: 0.3, ease: "easeOut" },
    },
  },
}

const leftVariants: Variants = {
  rest: { x: 0, d: "M118 138.406L99 138.406" },
  hover: {
    x: [0, OVERSHOOT, TRAVEL],
    d: `M${118 - INSET} 138.406L${99 + INSET} 138.406`,
    transition: {
      x: { duration: 0.3, ease: "easeOut", times: [0, 0.3, 1] },
      d: { duration: 0.3, ease: "easeOut" },
    },
  },
}

const rightVariants: Variants = {
  rest: { x: 0, d: "M317 138.406L298 138.406" },
  hover: {
    x: [0, -OVERSHOOT, -TRAVEL],
    d: `M${317 - INSET} 138.406L${298 + INSET} 138.406`,
    transition: {
      x: { duration: 0.3, ease: "easeOut", times: [0, 0.3, 1] },
      d: { duration: 0.3, ease: "easeOut" },
    },
  },
}

// Corner brackets move diagonally toward the eye by the same TRAVEL/OVERSHOOT
// extent as the top/bottom/left/right ticks, combined on both axes, plus an
// extra nudge equal to their own stroke width since a diagonal move along a
// straight-line path reads as smaller than an axis-aligned one of the same size.
const bounceTransition: Transition = { duration: 0.3, ease: "easeOut", times: [0, 0.3, 1] }
const CORNER_STROKE_WIDTH = 3
const CORNER_TRAVEL = TRAVEL + CORNER_STROKE_WIDTH
const CORNER_OVERSHOOT = OVERSHOOT + CORNER_STROKE_WIDTH

// Each bracket has one end anchored at its rounded joint (left untouched) and
// one free end far from it - that free end insets inward, shrinking the arm
// while the joint geometry stays put. Their arms (~48 units) are long enough
// to take a much bigger inset than the short ticks without crossing over.
const dTransition: Transition = { duration: 0.3, ease: "easeOut" }
const CORNER_INSET = INSET * 4

const topRightVariants: Variants = {
  rest: {
    x: 0,
    y: 0,
    d: "M245 30.4111H293.185C301.123 30.2279 317 34.6978 317 54.0429C317 73.388 317 94.3453 317 102.406",
  },
  hover: {
    x: [0, -CORNER_OVERSHOOT, -CORNER_TRAVEL],
    y: [0, CORNER_OVERSHOOT, CORNER_TRAVEL],
    d: `M${245 + CORNER_INSET} 30.4111H293.185C301.123 30.2279 317 34.6978 317 54.0429C317 73.388 317 94.3453 317 ${102.406 - CORNER_INSET}`,
    transition: { x: bounceTransition, y: bounceTransition, d: dTransition },
  },
}

const topLeftVariants: Variants = {
  rest: {
    x: 0,
    y: 0,
    d: "M99.0053 102.406L99.0053 54.2211C98.8221 46.2827 103.292 30.4058 122.637 30.4058C141.982 30.4058 162.94 30.4058 171 30.4058",
  },
  hover: {
    x: [0, CORNER_OVERSHOOT, CORNER_TRAVEL],
    y: [0, CORNER_OVERSHOOT, CORNER_TRAVEL],
    d: `M99.0053 ${102.406 - CORNER_INSET}L99.0053 54.2211C98.8221 46.2827 103.292 30.4058 122.637 30.4058C141.982 30.4058 162.94 30.4058 ${171 - CORNER_INSET} 30.4058`,
    transition: { x: bounceTransition, y: bounceTransition, d: dTransition },
  },
}

const bottomLeftVariants: Variants = {
  rest: {
    x: 0,
    y: 0,
    d: "M171 248.4L122.815 248.4C114.877 248.586 99 244.054 99 224.44C99 204.827 99 183.578 99 175.406",
  },
  hover: {
    x: [0, CORNER_OVERSHOOT, CORNER_TRAVEL],
    y: [0, -CORNER_OVERSHOOT, -CORNER_TRAVEL],
    d: `M${171 - CORNER_INSET} 248.4L122.815 248.4C114.877 248.586 99 244.054 99 224.44C99 204.827 99 183.578 99 ${175.406 + CORNER_INSET}`,
    transition: { x: bounceTransition, y: bounceTransition, d: dTransition },
  },
}

export const FatigueDetectionAnimation = ({ hover }: { hover?: boolean }) => (
  <motion.div
    initial="rest"
    animate={hover ? "hover" : "rest"}
    className="w-full aspect-[41/28] relative [&_svg]:absolute [&_svg]:w-full [&_svg]:h-auto overflow-hidden"
  >
    <svg width="408" height="276" viewBox="0 0 408 276" fill="none" xmlns="http://www.w3.org/2000/svg">
      <motion.path
        className="top-right"
        variants={topRightVariants}
        stroke="#4E4E4E"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <motion.path
        className="top-left"
        variants={topLeftVariants}
        stroke="#4E4E4E"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <motion.path
        className="bottom-left"
        variants={bottomLeftVariants}
        stroke="#4E4E4E"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <motion.path
        className="top"
        variants={topVariants}
        stroke="#4E4E4E"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <motion.path
        className="bottom"
        variants={bottomVariants}
        stroke="#4E4E4E"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <motion.path
        className="left"
        variants={leftVariants}
        stroke="#4E4E4E"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <motion.path
        className="right"
        variants={rightVariants}
        stroke="#4E4E4E"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>

    <motion.svg
      className="eye"
      variants={eyeVariants}
      width="408"
      height="276"
      viewBox="0 0 408 276"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M254.203 138.906C254.203 138.906 234.133 155.983 209.375 155.983C184.617 155.983 164.547 138.906 164.547 138.906C164.547 138.906 184.617 121.829 209.375 121.829C234.133 121.829 254.203 138.906 254.203 138.906Z"
        fill="#F3F3F3"
      />
      <ellipse cx="209.376" cy="139.759" rx="14.9427" ry="14.5158" fill="#222222" />
    </motion.svg>

    <motion.svg
      className="rotate"
      variants={rotateVariants}
      width="408"
      height="276"
      viewBox="0 0 408 276"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="255.863"
        y="196.186"
        width="13.3631"
        height="13.3631"
        transform="rotate(-45 255.863 196.186)"
        fill="#494F55"
      />
      <rect
        x="274.387"
        y="191.19"
        width="58.8496"
        height="19.8928"
        rx="9.94641"
        transform="rotate(45 274.387 191.19)"
        fill="#D9D9D9"
      />
      <circle cx="207.5" cy="138.181" r="72.5" stroke="#8E8E8E" strokeWidth="8" />
    </motion.svg>
  </motion.div>
)
